import React from 'react';
import { Box, Typography } from '@mui/material';
import { SportEvent } from '../types/SportEventTypes';
import { RegularEventItem } from './RegularEventItem';
import { getScore } from './PlayByPlayUtils';
import { inlineScoreContainerStyle, inlineScoreTextStyle } from '../styles/playByPlayStyles';

interface ScoreEventItemProps {
  event: SportEvent;
  eventIndex: number;
  teamLogoPath: string;
  teamColor: string;
  gameTimePrefix?: string;
  gameTime: string;
  description: string;
  onDebug: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  onClipCreate: (event: SportEvent) => void;
  clipGroupCount?: number;
  isNew?: boolean;
  isAhead?: boolean;
}

// Colors lifted directly from the NBA design: the scoring team's total is
// bright, the opponent's is dimmed to the same gray used for the row's time.
const SCORE_BRIGHT = '#DBDBE1';
const SCORE_DIM = '#9797A5';

/**
 * Inline scoring-event row for basketball. Renders the standard event row
 * (RegularEventItem chrome) with the running score pinned to the right edge,
 * the scoring team's total highlighted.
 *
 * This is deliberately NOT the full-width colored GoalEventItem scoreboard
 * that NHL goals use: per the NBA design a made basket / free throw stays in
 * the normal timeline flow and only annotates the score. It's selected via the
 * NBA module's `pickRenderer` hook (see sports/nba/index.ts), so NHL and every
 * other sport are untouched.
 *
 * Score order matches the GoalEventItem scoreboard (away then home). The side
 * that just scored comes from `payload.data.scorer` ('HOME' | 'AWAY'); when
 * that's missing we can't attribute the point, so both totals render bright.
 */
export const ScoreEventItem: React.FC<ScoreEventItemProps> = (props) => {
  const { event } = props;
  const score = getScore(event);
  const scorer = String((event.payload as any)?.data?.scorer ?? '').toUpperCase();
  const scorerKnown = scorer === 'HOME' || scorer === 'AWAY';

  const sideSx = (side: 'HOME' | 'AWAY') => {
    const highlighted = !scorerKnown || scorer === side;
    return {
      ...inlineScoreTextStyle,
      color: highlighted ? SCORE_BRIGHT : SCORE_DIM,
      fontWeight: highlighted ? 500 : 400,
    };
  };

  const scoreCluster = score ? (
    <Box sx={inlineScoreContainerStyle}>
      <Typography component="span" sx={sideSx('AWAY')}>
        {score.away}
      </Typography>
      <Typography component="span" sx={sideSx('HOME')}>
        {score.home}
      </Typography>
    </Box>
  ) : null;

  return <RegularEventItem {...props} rightAccessory={scoreCluster} />;
};
